import React, { useEffect, useState } from 'react';
import { useBrandingColors, getButtonInlineStyles, getButtonHoverStyles } from '../../hooks/useBrandingColors';
import {
  companiesManagementService,
  STATUS_LABELS,
  STATUS_COLORS,
  type Company,
} from '../../services/companiesManagementService';
import {
  labelCls, inputBase, tariffLabelCls, TARIFF_MODULES,
  CloseIcon, QuestionIcon, ChevronUp, ChevronDown, InfoTooltip,
  maskDate, toIso, isoToDisplay, isDateInvalid,
} from './companyModalUi';
import CustomCheckbox from '../ui/CustomCheckbox';

interface Props {
  company: Company;
  onClose: () => void;
  onSaved: () => void;
}

const STATUSES = ['ACTIVE', 'PILOT', 'SUSPENDED', 'INACTIVE'] as const;

// Индикатор статуса: цветная точка, а для «Пилотная» — фиолетовый подарок (#6E3FF3) по макету.
const StatusIndicator: React.FC<{ s: string; big?: boolean }> = ({ s, big }) =>
  s === 'PILOT' ? (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="#6E3FF3">
      <path d="M15 2a4 4 0 0 1 3.464 6.001L23 8v2h-2v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-9H1V8l4.536.001A4 4 0 0 1 12 3.355 3.983 3.983 0 0 1 15 2zm-4 8H5v8h6v-8zm8 0h-6v8h6v-8zM9 4a2 2 0 0 0-.15 3.995L9 8h2V6a2 2 0 0 0-1.85-1.995L9 4zm6 0a2 2 0 0 0-1.995 1.85L13 6v2h2a2 2 0 0 0 .15-3.995L15 4z" />
    </svg>
  ) : (
    <span
      className={`${big ? 'w-2.5 h-2.5' : 'w-2 h-2'} rounded-full border border-black/20`}
      style={{ backgroundColor: STATUS_COLORS[s] }}
    />
  );

export const EditCompanyModal: React.FC<Props> = ({ company, onClose, onSaved }) => {
  const colors = useBrandingColors();

  const [name, setName] = useState(company.name);
  const [ownerEmail, setOwnerEmail] = useState(company.ownerEmail ?? '');
  const [description, setDescription] = useState(company.description ?? '');
  const [status, setStatus] = useState(company.status);
  const [statusOpen, setStatusOpen] = useState(false);
  const [subscriptionDate, setSubscriptionDate] = useState(isoToDisplay(company.subscriptionStartDate));
  const [maxAccounts, setMaxAccounts] = useState(String(company.maxAccounts ?? 10));
  const [tariffs, setTariffs] = useState({ searchCreditsPriceKopecks: '0', dialogCreditsPriceKopecks: '0', interviewCreditsPriceKopecks: '0' });
  const [relevanterEnabled, setRelevanterEnabled] = useState(company.relevanterEnabled ?? true);
  const [atsEnabled, setAtsEnabled] = useState(company.atsEnabled ?? false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Загружаем текущие тарифы
  useEffect(() => {
    if (!company.id) return;
    companiesManagementService.getPricing(company.id).then(pricing => {
      // API возвращает kopecks — конвертируем в рубли для отображения (лейблы ₽/шт)
      setTariffs({
        searchCreditsPriceKopecks: String(Math.round((pricing.searchCreditsPriceKopecks ?? 0) / 100)),
        dialogCreditsPriceKopecks: String(Math.round((pricing.dialogCreditsPriceKopecks ?? 0) / 100)),
        interviewCreditsPriceKopecks: String(Math.round((pricing.interviewCreditsPriceKopecks ?? 0) / 100)),
      });
    }).catch(() => {});
  }, [company.id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !ownerEmail.trim() || !company.id) return;

    // REL-196: нельзя выставить лимит мест меньше уже заведённых аккаунтов.
    const currentAccounts = company.accountsCount ?? 0;
    const nextMax = parseInt(maxAccounts, 10);
    if (!Number.isNaN(nextMax) && nextMax < currentAccounts) {
      setError(
        `Текущее количество аккаунтов (${currentAccounts}) больше введённого значения (${nextMax}). ` +
          'Удалите часть аккаунтов из профиля компании.',
      );
      return;
    }

    setLoading(true);
    setError(null);
    // Форма хранит рубли (₽/шт) — конвертируем в копейки для API
    const priceKopecks = {
      searchCreditsPriceKopecks: (parseInt(tariffs.searchCreditsPriceKopecks) || 0) * 100,
      dialogCreditsPriceKopecks: (parseInt(tariffs.dialogCreditsPriceKopecks) || 0) * 100,
      interviewCreditsPriceKopecks: (parseInt(tariffs.interviewCreditsPriceKopecks) || 0) * 100,
    };
    try {
      await companiesManagementService.updateCompany(company.id, {
        name: name.trim(),
        ownerEmail: ownerEmail.trim() || undefined,
        description: description.trim() || undefined,
        status,
        subscriptionStartDate: toIso(subscriptionDate),
        maxAccounts: parseInt(maxAccounts) || 10,
        relevanterEnabled,
        atsEnabled,
        ...priceKopecks,
      });
      // Тарифы читаются из отдельного эндпойнта (GET /pricing), поэтому пишем их и через
      // выделенный PUT /pricing — иначе обновление компании может «проглотить» цены и сохранение
      // не отразится в списке/аналитике (баг «сохранила цены, но ничего не поменялось»).
      try {
        await companiesManagementService.setPricing(company.id, priceKopecks);
      } catch { /* не блокируем сохранение, если pricing-эндпойнт недоступен */ }
      onSaved();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Ошибка обновления');
    } finally {
      setLoading(false);
    }
  };

  const stepTariff = (key: keyof typeof tariffs, delta: number) =>
    setTariffs(prev => {
      const next = Math.max(0, (parseInt(prev[key], 10) || 0) + delta);
      return { ...prev, [key]: String(next) };
    });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="w-[540px] max-w-[calc(100vw-32px)] max-h-[90vh] overflow-y-auto rounded-[20px] bg-[#F5F6F1]">
        <form onSubmit={handleSubmit} className="flex flex-col gap-6 p-6">
          {/* Заголовок */}
          <div className="flex items-center justify-between h-8">
            <span className="w-8 shrink-0" />
            <h2 className="flex-1 text-center text-[24px] leading-[32px] font-medium tracking-[-0.015em] text-black">
              Редактирование компании
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 shrink-0 flex items-center justify-center text-[#868C98] hover:text-[#0A0D14]"
              aria-label="Закрыть"
            >
              <CloseIcon />
            </button>
          </div>

          <div className="h-px bg-[#EBE7DC]" />

          <div className="flex flex-col gap-6">
            {/* Название + Email */}
            <div className="flex gap-4">
              <div className="flex-1 flex flex-col gap-3">
                <label className={labelCls}>
                  Название компании <span className="text-[#DF1C41]">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="Введите название"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  className={`${inputBase} px-3 text-[14px] leading-[20px]`}
                />
              </div>
              <div className="flex-1 flex flex-col gap-3">
                <label className={labelCls}>
                  Email владельца (логин) <span className="text-[#DF1C41]">*</span>
                </label>
                <input
                  type="email"
                  required
                  placeholder="hr@wmtgroup.ru"
                  value={ownerEmail}
                  onChange={e => setOwnerEmail(e.target.value)}
                  className={`${inputBase} px-3 text-[14px] leading-[20px]`}
                />
              </div>
            </div>

            {/* Описание */}
            <div className="flex flex-col gap-3">
              <label className={labelCls}>Описание деятельности компании</label>
              <textarea
                placeholder="Опишите, чем занимается ваша компания..."
                value={description}
                onChange={e => setDescription(e.target.value)}
                className={`${inputBase} h-[60px] px-3 py-2.5 text-[14px] leading-[20px] resize-none`}
              />
            </div>

            {/* Дата начала подписки + Статус + Места */}
            <div className="flex gap-3">
              <div className="flex-1 flex flex-col gap-3">
                <label className={labelCls}>Дата начала подписки</label>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="дд.мм.гггг"
                  value={subscriptionDate}
                  onChange={e => setSubscriptionDate(maskDate(e.target.value))}
                  className={`${inputBase} !h-11 px-3 text-[16px] leading-[20px] ${isDateInvalid(subscriptionDate) ? '!border-[#DF1C41]' : ''}`}
                />
              </div>

              <div className="flex-1 flex flex-col gap-3">
                <label className={labelCls}>Статус компании</label>
                {/* Кастомный дропдаун статуса по макету (точки/подарок, не нативный select) */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setStatusOpen(o => !o)}
                    className={`${inputBase} !h-11 flex items-center gap-1 pl-2.5 pr-3 text-left ${statusOpen ? '!border-[#E16349]' : ''}`}
                  >
                    <span className="w-4 h-4 shrink-0 flex items-center justify-center">
                      <StatusIndicator s={status} />
                    </span>
                    <span className="flex-1 truncate text-[16px] leading-[20px] text-[#0A0D14]">{STATUS_LABELS[status]}</span>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="#868C98" className="shrink-0">
                      <path d="M12 13.172l4.95-4.95 1.414 1.414L12 16 5.636 9.636 7.05 8.222z" />
                    </svg>
                  </button>
                  {statusOpen && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setStatusOpen(false)} />
                      <div className="absolute z-20 top-[48px] left-0 w-[197px] rounded-[10px] border border-[#E2E4E9] bg-white py-2.5 shadow-[0px_16px_32px_-12px_rgba(88,92,95,0.1)]">
                        {STATUSES.map(s => (
                          <button
                            key={s}
                            type="button"
                            onClick={() => { setStatus(s as any); setStatusOpen(false); }}
                            className="flex w-full items-center gap-1 px-2.5 py-2.5 border-b border-[#F4F5F0] last:border-b-0 hover:bg-[#FAFAFA]"
                          >
                            <span className="w-4 h-4 shrink-0 flex items-center justify-center">
                              <StatusIndicator s={s} big />
                            </span>
                            <span className="px-1 text-[14px] leading-[20px] font-medium text-[#525866]">{STATUS_LABELS[s]}</span>
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="w-[72px] shrink-0 flex flex-col gap-3">
                <label className={`${labelCls} flex items-center gap-1 whitespace-nowrap`}>
                  Места{' '}
                  <InfoTooltip text="Максимальное количество аккаунтов рекрутеров">
                    <span className="cursor-help">
                      <QuestionIcon />
                    </span>
                  </InfoTooltip>
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  aria-label="Места"
                  value={maxAccounts}
                  onChange={e => setMaxAccounts(e.target.value.replace(/[^\d]/g, ''))}
                  className={`${inputBase} !h-11 px-3 text-[16px] leading-[20px] text-center`}
                />
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <p className={labelCls}>Доступные модули</p>
              <div className="grid grid-cols-2 gap-3 rounded-[12px] border border-[#E2E4E9] bg-white px-4 py-3">
                <CustomCheckbox
                  checked={relevanterEnabled}
                  onChange={setRelevanterEnabled}
                  label="Релевантер"
                />
                <CustomCheckbox
                  checked={atsEnabled}
                  onChange={setAtsEnabled}
                  label="ATS"
                />
              </div>
            </div>

            {/* Настройка тарифов на ии модули */}
            <div className="flex flex-col gap-3">
              <p className={labelCls}>Настройка тарифов на ии модули</p>
              <div className="flex gap-3">
                {TARIFF_MODULES.map(m => (
                  <div key={m.key} className="flex-1 flex flex-col gap-2">
                    <label className={tariffLabelCls}>{m.label}</label>
                    <div className="relative">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={tariffs[m.key]}
                        onChange={e => setTariffs(prev => ({ ...prev, [m.key]: e.target.value.replace(/[^\d]/g, '') }))}
                        className={`${inputBase} pl-4 pr-7 text-[16px] leading-[20px]`}
                      />
                      <div className="absolute right-2 top-1/2 -translate-y-1/2 flex flex-col">
                        <button type="button" onClick={() => stepTariff(m.key, 1)} aria-label="Увеличить" className="-mb-1 h-3.5 flex items-center">
                          <ChevronUp />
                        </button>
                        <button type="button" onClick={() => stepTariff(m.key, -1)} aria-label="Уменьшить" className="-mt-1 h-3.5 flex items-center">
                          <ChevronDown />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {error && <p className="text-[14px] text-[#DF1C41]">{error}</p>}
          </div>

          <div className="h-px bg-[#EBE7DC]" />

          {/* Кнопки */}
          <div className="flex gap-2.5">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 h-12 rounded-[12px] bg-[#E9EAE4] text-[#7D776C] text-[15px] leading-[28px] font-semibold tracking-[0.01em] hover:bg-[#e0e1da] transition-colors"
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={loading || !name.trim() || !ownerEmail.trim()}
              className="flex-1 h-12 rounded-[12px] text-white text-[15px] leading-[28px] font-semibold tracking-[0.01em] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              style={getButtonInlineStyles(colors, 'primary')}
              onMouseEnter={e => { if (!loading) Object.assign(e.currentTarget.style, getButtonHoverStyles(colors, 'primary')); }}
              onMouseLeave={e => { if (!loading) Object.assign(e.currentTarget.style, getButtonInlineStyles(colors, 'primary')); }}
            >
              {loading ? 'Сохранение...' : 'Сохранить'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
